// Import Packages
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';

// Import Pages
import { FileInputMateDrive, TabPanel } from '../../../FlowConfigurationInputs';
import { CodeEditor } from '../../../FlowConfigurationInputs/CodeEditor/CodeEditor';
import { RadioOption, RadioPicker } from '../../../FlowConfigurationInputs/RadioPicker/RadioPicker';
import { TextInput } from '../../../FlowConfigurationInputs/TextInput/NewTextInput';
import VariablePicker from '../../../FlowConfigurationInputs/VariablePicker/VariablePicker';
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';

// Import Material UI
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Chip, IconButton, TextField, Dialog } from '@material-ui/core';

// Import Recoil
import { saveAvailable, initialActions, changedFields } from "../../../../recoil-store/recoil-state";

// Import Helpers
import { CheckIfFlowUpdated } from '../../../../helpers/helper';



const useStyles = makeStyles(theme => ({

    option: {
        '& > span': {
            marginRight: 10,
            fontSize: 12,
        },
        fontSize: 13,
        fontWeight: 600,
        color: '#46474E',
    },
    inputRoot: {
        "& fieldset": {
            borderColor: '#E6E6E6',
            borderWidth: '1.1px'
        }
    },
    popupIndicator: {
        color: "#B0B0B0",
        height: "10px",
        width: "10px"
    }
}));


/**
 * @Description This function lets you return component according to the name of the incoming action.
 * @Param       action - object
 * @Param       variables - object variable list
 * @Param       setOpenVariable - open variable function that allows us to open
 * @param       showAlert - show alert
 * @Returns     Component
 * @Author      Abdullah Onur Yeşil
 */
function RemoveKeys({ action, setOpenVariable, variables, automId, visible }) {
    const [keys, setKeys] = useState([]);

    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);

    const label = 'CONTENT.CONFIGURATION-FORM.OBJECT.REMOVE-KEYS.';
    const classes = useStyles();
    const { t } = useTranslation();

    useEffect(() => {
        if (!action.fields.keys) {
            action.fields.keys = { value: [], isRequired: true, type: "ARRAY" };
        }
        setKeys(action.fields.keys.value);
    }, [action.id]);

    const handleChange = (e, newValue) => {
        const field = initialActionsData[action.id]?.fields.keys;
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated('keys', action, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState);
        action.fields.keys.value = newValue;
        setKeys(newValue);
    }

    return (
        <div>
            <div className={`conf-field-container ${visible ? "display-none" : ""}`}>
                <FieldLabel name={t(label + "SOURCE-OBJECT")} is_required />
                <TextInput
                    action={action}
                    inputName="source"
                    variables={variables}
                    setOpenVariable={setOpenVariable}
                    isRequired
                    hideRequired
                />
                <small className="conf-field-desc"><em>##xObjectVariable[user]##</em></small>
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + "KEYS")} is_required />
                <Autocomplete
                    classes={{
                        option: classes.option,
                        inputRoot: classes.inputRoot,
                        popupIndicator: classes.popupIndicator
                    }}
                    style={{ width: "90%", position: 'relative', background: '#FBFBFB' }}
                    freeSolo
                    multiple
                    onChange={handleChange}
                    value={keys}
                    size="small"
                    options={[]}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option}
                    renderInput={params => <TextField {...params} variant="outlined" />}
                />
                <small className='conf-field-desc'><em>{t(label + "KEYS-DESC")}</em></small>

            </div>

            <div className="conf-field-container">
                <FieldLabel name={t(label + "ASSIGN-REMOVED-KEYS")} />
                <VariablePicker action={action} inputName="set"
                    variables={variables} setOpenVariable={setOpenVariable}
                    filter="object"
                />
                <small><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'object' })}</em></small>
                <div><small><em>{t('CONTENT.GENERAL.EXAMPLE-RESPONSE')}</em></small></div>
                <div><small><em>{"{"}</em></small></div>
                <div><small><em>{" “key1”: ”test”,"}</em></small></div>
                <div><small><em>{" “key2”: [],"}</em></small></div>
                <div><small><em>{" ”key3”: {}"}</em></small></div>
                <div><small><em>{"}"}</em></small></div>
            </div>
        </div>
    );
}

export default RemoveKeys;